import { get, pick, reduce, set, update, chain, map, filter, omit, last, find, merge } from "lodash"
import { produce } from "immer"
import { createSelector } from "reselect"
import { percentageFormatter, numberFormatter } from "helpers/factor-view"
import { getModelCategories, getModelFactors } from "selectors/modelMetadata"
import { computeSelectedMetricType, computeMetricTypeOptions } from "helpers/assets"
import { assetDollarFormatter } from "helpers/formatters/numbers"
import { METRIC_TYPES } from "helpers/assets/constants"
import { getBaseMetricTypeOptions } from "selectors/assets/metricTypes"
import { getDefaultMetricUnits } from "modules/default-metric-units/selectors"
import { DEFAULT_STATE } from "../model"

const EMPTY_OBJECT = {}

// Performance is used for the Cumulative Returns
// PeriodPerformance for the Annualized returns
const getRawPerformance = state => get(state, "global.data.analyzeFactorsPerformance.data.model.simulation.performance", EMPTY_OBJECT)
const getRawPerformanceFactors = state => get(state, "global.data.analyzeFactorsPerformance.data.model.simulation.periodPerformance.attribution.factors", EMPTY_OBJECT)
const getPeriodPerformance = state => get(state, "global.data.analyzeFactorsPerformance.data.model.simulation.periodPerformance", EMPTY_OBJECT)
const getPeriodPerformanceSummary = state => get(state, "global.data.analyzeFactorsPerformance.data.model.simulation.periodPerformance.attribution.summary", EMPTY_OBJECT)
const getRawSelectedFactorCategory = state => get(state, "modules.analyzeFactorsPerformance.selectedFactorCategory")
const getRawSelectedMetricType = state => get(state, "modules.analyzeFactorsPerformance.selectedMetricType")

function mapPerformanceItem(performanceItem, metricField) {
  return performanceItem ? {
    total: get(performanceItem, ["totalReturn", metricField]),
    attribution: {
      summary: {
        factors: get(performanceItem, ["attribution", "summary", "factors", metricField]),
        specific: get(performanceItem, ["attribution", "summary", "specific", metricField]),
      },
      factors: map(get(performanceItem, "attribution.factors"), (factor) => ({
        id: factor.id,
        cumulativeReturn: get(factor, ["return", metricField]),
      })),
    },
  } : {}
}

export const getMetricTypeOptions = createSelector(
  [getBaseMetricTypeOptions, getDefaultMetricUnits],
  (baseMetricTypeOptions, defaultMetricType) => {
    return computeMetricTypeOptions(baseMetricTypeOptions, "performance", false, null, null, defaultMetricType)
  },
)

export const getSelectedMetricType = createSelector(
  [getRawSelectedMetricType, getMetricTypeOptions],
  computeSelectedMetricType,
)

const getMetricField = createSelector(
  [getSelectedMetricType],
  (metricType) => metricType === METRIC_TYPES.CURRENCY_PERFORMANCE ? "cumulativeCurrency" : "cumulativePercent",
)

const getPerformance = createSelector(
  [getRawPerformance, getMetricField],
  (performance, metricField) => mapPerformanceItem(last(performance), metricField),
)

const getCumulativeReturnFactors = createSelector(
  [getPerformance],
  (performance) => {
    return get(performance, "attribution.factors")
  },
)

const getCumulativeReturnSummary = createSelector(
  [getPerformance],
  (performance) => get(performance, "attribution.summary"),
)

export const getMappedPerformanceFactors = createSelector(
  [getRawPerformanceFactors, getCumulativeReturnFactors],
  // performanceFactors is a direct reference to Redux state (attribution.factors); lodash merge()
  // mutates its first arg and would trip RTK's immutableCheck on SET_METRIC_TYPE. Deep-merge the
  // per-index cumulativeReturn onto an immer draft — preserving the original deep-merge semantics
  // while leaving the store untouched.
  (performanceFactors, cumulativeReturnFactors) => chain(produce(performanceFactors, (draft) => {
    merge(draft, cumulativeReturnFactors)
  }))
    .mapKeys((factor) => factor.id)
    .value(),
)

export const getPerformanceSummary = createSelector(
  [getPerformance, getPeriodPerformance],
  (performance, periodPerformance) => chain(performance)
    .omit("attribution")
    .merge(omit(periodPerformance, "attribution"))
    .value(),
)

export const getTotalRow = createSelector(
  [getPerformanceSummary, getSelectedMetricType],
  (summary, metricType) => {
    const performanceMetricFormatter = metricType === METRIC_TYPES.CURRENCY_PERFORMANCE ? assetDollarFormatter : percentageFormatter
    return [
      "Total",
      { value: performanceMetricFormatter(get(summary, "total")), tooltip: `Annualized return: ${percentageFormatter(get(summary, "annualizedReturns") / 100)}` },
      percentageFormatter(get(summary, "annualizedVolatility") / 100),
      numberFormatter(get(summary, "informationRatio")),
    ]
  },
)

function computeSummary(summary, key) {
  return {
    id: key,
    cumulativeReturn: get(summary, [key, "cumulativeReturn"]),
    annualizedReturns: get(summary, [key, "annualizedReturns"]),
    annualizedVolatility: get(summary, [key, "annualizedVolatility"]),
    informationRatio: get(summary, [key, "informationRatio"]),
  }
}

export const getSummaryRows = createSelector(
  [getCumulativeReturnSummary, getPeriodPerformanceSummary],
  (cumulativeReturnSummary, periodPerformanceSummary) => {
    // periodPerformanceSummary is a direct reference to Redux state; lodash set() on it trips RTK's
    // immutableCheck (the crash surfaces on the next filter toggle). Write the cumulativeReturn
    // overrides onto an immer draft so a fresh object is returned with every other key preserved.
    const attributionSummary = produce(periodPerformanceSummary, (draft) => {
      set(draft, "factors.cumulativeReturn", get(cumulativeReturnSummary, "factors"))
      set(draft, "specific.cumulativeReturn", get(cumulativeReturnSummary, "specific"))
    })

    return [
      computeSummary(attributionSummary, "specific"),
      computeSummary(attributionSummary, "factors"),
    ]
  },
)

// Contains only factors that have non-nil values for at least one of the following [annualizedReturns, annualizedVolatility, informationRatio]
export const getFactorsWithMetadata = createSelector(
  [getMappedPerformanceFactors, getModelFactors],
  (mappedFactors, rawFactors) => {
    const factorHasValues = (factor) => get(factor, "cumulativeReturn") || get(factor, "annualizedVolatility") || get(factor, "informationRatio")

    return reduce(rawFactors, (factors, rawFactor, factorId) => factorHasValues(get(mappedFactors, factorId))
      ? set(factors, factorId, {
        id: factorId,
        label: rawFactor.alias || rawFactor.label,
        category: get(rawFactors, [factorId, "category"]),
        ...pick(get(mappedFactors, factorId), ["cumulativeReturn", "annualizedReturns", "annualizedVolatility", "informationRatio"]),
      }) : factors, {})
  },
)

export const getFactorCategories = createSelector(
  [getFactorsWithMetadata],
  (factors) => reduce(factors, (categories, factor) => update(categories, factor.category, existing => {
    return {
      cumulativeReturn: get(existing, "cumulativeReturn", 0) + get(factor, "cumulativeReturn", 0),
      annualizedReturns: get(existing, "annualizedReturns", 0) + get(factor, "annualizedReturns", 0),
      // annualizedVolatility & informationRatio are not cumulative and the api doesn't support these yet
      annualizedVolatility: null,
      informationRatio: null,
    }
  }), {}),
)

// Contains only non-nil factors categories
export const getCategories = createSelector(
  [getModelCategories, getSummaryRows, getFactorCategories],
  (categories, summaryRows, factorCategories) => [
    ...summaryRows,
    ...chain(categories)
      .map((category) => ({
        id: category.id,
        ...get(factorCategories, category.id, {}),
      }))
      .filter(category => category.annualizedReturns || category.annualizedVolatility || category.informationRatio)
      .value(),

  ],
)

export const getSelectedFactorCategory = createSelector(
  [getRawSelectedFactorCategory, getCategories],
  (selected, categories) => selected === DEFAULT_STATE.selectedFactorCategory || find(categories, ({ id }) => id === selected) ? selected : DEFAULT_STATE.selectedFactorCategory,
)

export const getCurrentFactors = createSelector(
  [getFactorsWithMetadata, getSelectedFactorCategory],
  (factors, selectedCategory) => selectedCategory === "factors" ? map(factors) : filter(factors, factor => factor.category === selectedCategory),
)
